import React, { useState } from 'react';
import { FileText, Clock, CheckCircle, ChevronRight, Save } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
interface EvaluationsProps {
  isManager?: boolean;
}
interface EvaluationResponse {
  questionId: string;
  rating?: number;
  comment?: string;
}
export function Evaluations({
  isManager = false
}: EvaluationsProps) {
  const {
    showToast
  } = useToast();
  const [activeSection, setActiveSection] = useState(0);
  const [responses, setResponses] = useState<Record<string, EvaluationResponse>>({});
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const sections = [{
    id: 'auto-evaluation',
    title: 'Auto-évaluation',
    description: 'Évaluez vos performances et réalisations',
    status: 'completed' as const
  }, {
    id: 'objectifs',
    title: 'Objectifs',
    description: 'Revue de vos objectifs annuels',
    status: 'completed' as const
  }, {
    id: 'competences',
    title: 'Compétences',
    description: 'Évaluation de vos compétences clés',
    status: 'in-progress' as const
  }, {
    id: 'developpement',
    title: 'Développement',
    description: 'Plan de développement professionnel',
    status: 'pending' as const
  }, {
    id: 'commentaires',
    title: 'Commentaires',
    description: 'Commentaires et suggestions',
    status: 'pending' as const
  }];
  const competencesQuestions = [{
    id: 'comp_1',
    category: 'Compétences techniques',
    question: 'Maîtrise des outils et technologies de mon domaine'
  }, {
    id: 'comp_2',
    category: 'Compétences techniques',
    question: 'Capacité à résoudre des problèmes complexes'
  }, {
    id: 'comp_3',
    category: 'Communication',
    question: "Communication claire et efficace avec l'équipe"
  }, {
    id: 'comp_4',
    category: 'Communication',
    question: 'Écoute active et prise en compte des feedbacks'
  }, {
    id: 'comp_5',
    category: 'Collaboration',
    question: "Travail d'équipe et collaboration"
  }, {
    id: 'comp_6',
    category: 'Collaboration',
    question: 'Partage de connaissances avec les collègues'
  }, {
    id: 'comp_7',
    category: 'Leadership',
    question: "Prise d'initiative et proactivité"
  }, {
    id: 'comp_8',
    category: 'Leadership',
    question: 'Capacité à influencer et motiver les autres'
  }];
  const teamMembers = [{
    id: '1',
    name: 'Marie Dupont',
    position: 'Développeuse Frontend',
    status: 'in-progress'
  }, {
    id: '2',
    name: 'Thomas Petit',
    position: 'Développeur Backend',
    status: 'pending'
  }, {
    id: '3',
    name: 'Sophie Bernard',
    position: 'Designer UX',
    status: 'completed'
  }];
  const handleRatingChange = (questionId: string, rating: number) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        questionId,
        rating
      }
    }));
  };
  const handleCommentChange = (questionId: string, comment: string) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        questionId,
        comment
      }
    }));
  };
  const handleSave = () => {
    showToast('Progression sauvegardée avec succès', 'success');
  };
  const handleSubmit = () => {
    showToast('Évaluation soumise avec succès', 'success');
  };
  const getSectionProgress = () => {
    const completedSections = sections.filter(s => s.status === 'completed').length;
    return Math.round(completedSections / sections.length * 100);
  };
  if (isManager && !selectedEmployee) {
    return <div className="pb-20 md:pb-0" data-id="element-554">
        <div className="mb-6" data-id="element-555">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-556">
            Gestion des évaluations
          </h1>
          <p className="text-gray-600" data-id="element-557">
            Suivez et gérez les évaluations de votre équipe
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-558">
          <div className="flex items-center justify-between mb-4" data-id="element-559">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-560">
              Campagne d'évaluation 2023
            </h2>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium" data-id="element-561">
              En cours
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-562">
            <div className="bg-green-50 p-4 rounded-lg" data-id="element-563">
              <div className="text-2xl font-bold text-green-600 mb-1" data-id="element-564">1</div>
              <div className="text-sm text-green-800" data-id="element-565">Complétées</div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg" data-id="element-566">
              <div className="text-2xl font-bold text-blue-600 mb-1" data-id="element-567">1</div>
              <div className="text-sm text-blue-800" data-id="element-568">En cours</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg" data-id="element-569">
              <div className="text-2xl font-bold text-gray-600 mb-1" data-id="element-570">1</div>
              <div className="text-sm text-gray-800" data-id="element-571">Non démarrées</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-572">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-573">
            Évaluations de l'équipe
          </h2>
          <div className="space-y-4" data-id="element-574">
            {teamMembers.map(member => <div key={member.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-575">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-576">
                  <div data-id="element-577">
                    <h3 className="font-medium text-gray-900" data-id="element-578">{member.name}</h3>
                    <p className="text-sm text-gray-600" data-id="element-579">{member.position}</p>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-580">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${member.status === 'completed' ? 'bg-green-100 text-green-800' : member.status === 'in-progress' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-581">
                      {member.status === 'completed' ? 'Complétée' : member.status === 'in-progress' ? 'En cours' : 'Non démarrée'}
                    </span>
                    <button onClick={() => setSelectedEmployee(member.id)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-id="element-582">
                      {member.status === 'completed' ? 'Voir' : 'Évaluer'}
                    </button>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>;
  }
  return <div className="pb-20 md:pb-0" data-id="element-583">
      <div className="mb-6" data-id="element-584">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-585">
          {isManager ? `Évaluation - ${teamMembers.find(m => m.id === selectedEmployee)?.name}` : 'Mon évaluation annuelle 2023'}
        </h1>
        <p className="text-gray-600" data-id="element-586">
          {isManager ? 'Évaluez les performances de votre collaborateur' : 'Complétez votre auto-évaluation avant le 15 décembre 2023'}
        </p>
      </div>
      {isManager && <button onClick={() => setSelectedEmployee(null)} className="mb-4 text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-587">
          ← Retour à la liste
        </button>}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-588">
        <div className="flex justify-between items-center mb-2" data-id="element-589">
          <span className="text-sm font-medium text-gray-700" data-id="element-590">
            Progression globale
          </span>
          <span className="text-sm font-medium text-gray-700" data-id="element-591">
            {getSectionProgress()}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-592">
          <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{
          width: `${getSectionProgress()}%`
        }} data-id="element-593"></div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-594">
        <div className="lg:col-span-1" data-id="element-595">
          <div className="bg-white rounded-lg shadow-sm p-4" data-id="element-596">
            <h2 className="font-semibold text-gray-900 mb-4" data-id="element-597">Sections</h2>
            <div className="space-y-2" data-id="element-598">
              {sections.map((section, index) => <button key={section.id} onClick={() => setActiveSection(index)} className={`w-full text-left px-3 py-3 rounded-lg transition-colors ${activeSection === index ? 'bg-blue-50 border-l-4 border-blue-600' : 'hover:bg-gray-50'}`} data-id="element-599">
                  <div className="flex items-center justify-between" data-id="element-600">
                    <div data-id="element-601">
                      <div className="font-medium text-gray-900 text-sm" data-id="element-602">
                        {section.title}
                      </div>
                      <div className="text-xs text-gray-600 mt-1" data-id="element-603">
                        {section.description}
                      </div>
                    </div>
                    {section.status === 'completed' && <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" data-id="element-604" />}
                    {section.status === 'in-progress' && <Clock className="h-5 w-5 text-blue-600 flex-shrink-0" data-id="element-605" />}
                  </div>
                </button>)}
            </div>
          </div>
        </div>
        <div className="lg:col-span-3" data-id="element-606">
          <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-607">
            <h2 className="text-xl font-semibold text-gray-900 mb-6" data-id="element-608">
              {sections[activeSection].title}
            </h2>
            {activeSection === 0 && <div className="space-y-6" data-id="element-609">
                <div data-id="element-610">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-611">
                    Principales réalisations de l'année
                  </label>
                  <textarea rows={4} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors" placeholder="Décrivez vos principales réalisations et contributions..." data-id="element-612" />
                </div>
                <div data-id="element-613">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-614">
                    Défis rencontrés et comment vous les avez surmontés
                  </label>
                  <textarea rows={4} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors" placeholder="Partagez les défis que vous avez rencontrés..." data-id="element-615" />
                </div>
                <div data-id="element-616">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-617">
                    Points forts
                  </label>
                  <textarea rows={3} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors" placeholder="Quels sont vos principaux points forts?" data-id="element-618" />
                </div>
                <div data-id="element-619">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-620">
                    Axes d'amélioration
                  </label>
                  <textarea rows={3} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors" placeholder="Sur quels aspects souhaitez-vous vous améliorer?" data-id="element-621" />
                </div>
              </div>}
            {activeSection === 1 && <div className="space-y-6" data-id="element-622">
                <p className="text-gray-600 mb-6" data-id="element-623">
                  Évaluez l'atteinte de vos objectifs annuels
                </p>
                {[{
              title: 'Améliorer la satisfaction client',
              target: '85%',
              achieved: '88%'
            }, {
              title: 'Réduire le temps de traitement',
              target: '20%',
              achieved: '25%'
            }, {
              title: 'Développer mes compétences en leadership',
              target: 'Formation complétée',
              achieved: 'En cours'
            }].map((objective, index) => <div key={index} className="border border-gray-200 rounded-lg p-4" data-id="element-624">
                    <h3 className="font-medium text-gray-900 mb-3" data-id="element-625">
                      {objective.title}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-id="element-626">
                      <div data-id="element-627">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-628">
                          Objectif
                        </label>
                        <div className="text-sm font-medium text-gray-900" data-id="element-629">
                          {objective.target}
                        </div>
                      </div>
                      <div data-id="element-630">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-631">
                          Réalisé
                        </label>
                        <div className="text-sm font-medium text-gray-900" data-id="element-632">
                          {objective.achieved}
                        </div>
                      </div>
                    </div>
                    <div data-id="element-633">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-634">
                        Niveau d'atteinte
                      </label>
                      <div className="flex gap-2" data-id="element-635">
                        {[1, 2, 3, 4, 5].map(rating => <button key={rating} onClick={() => handleRatingChange(`obj_${index}`, rating)} className={`flex-1 py-2 rounded-lg border-2 transition-all ${responses[`obj_${index}`]?.rating === rating ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-636">
                            <div className="text-lg font-semibold" data-id="element-637">
                              {rating}
                            </div>
                          </button>)}
                      </div>
                      <div className="flex justify-between text-xs text-gray-600 mt-1" data-id="element-638">
                        <span data-id="element-639">Non atteint</span>
                        <span data-id="element-640">Dépassé</span>
                      </div>
                    </div>
                    <div className="mt-4" data-id="element-641">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-642">
                        Commentaires
                      </label>
                      <textarea rows={2} onChange={e => handleCommentChange(`obj_${index}`, e.target.value)} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0" placeholder="Ajoutez des précisions..." data-id="element-643" />
                    </div>
                  </div>)}
              </div>}
            {activeSection === 2 && <div className="space-y-6" data-id="element-644">
                <p className="text-gray-600 mb-6" data-id="element-645">
                  Évaluez vos compétences sur une échelle de 1 à 5
                </p>
                {competencesQuestions.map((question, index) => <div key={question.id} className="border-b border-gray-200 pb-6 last:border-b-0" data-id="element-646">
                    {(index === 0 || competencesQuestions[index - 1].category !== question.category) && <h3 className="font-semibold text-gray-900 mb-4" data-id="element-647">
                        {question.category}
                      </h3>}
                    <div className="ml-0 md:ml-4" data-id="element-648">
                      <p className="text-gray-900 mb-3" data-id="element-649">{question.question}</p>
                      <div className="flex gap-2 mb-3" data-id="element-650">
                        {[1, 2, 3, 4, 5].map(rating => <button key={rating} onClick={() => handleRatingChange(question.id, rating)} className={`flex-1 py-3 rounded-lg border-2 transition-all ${responses[question.id]?.rating === rating ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-651">
                            <div className="text-lg font-semibold" data-id="element-652">
                              {rating}
                            </div>
                          </button>)}
                      </div>
                      <div className="flex justify-between text-xs text-gray-600 mb-3" data-id="element-653">
                        <span data-id="element-654">À développer</span>
                        <span data-id="element-655">Expert</span>
                      </div>
                      <textarea rows={2} onChange={e => handleCommentChange(question.id, e.target.value)} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 text-sm" placeholder="Commentaire (optionnel)..." data-id="element-656" />
                    </div>
                  </div>)}
              </div>}
            {activeSection === 3 && <div className="space-y-6" data-id="element-657">
                <div data-id="element-658">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-659">
                    Objectifs pour l'année prochaine
                  </label>
                  <textarea rows={4} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors" placeholder="Quels sont vos objectifs pour l'année prochaine?" data-id="element-660" />
                </div>
                <div data-id="element-661">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-662">
                    Compétences à développer
                  </label>
                  <textarea rows={3} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors" placeholder="Quelles compétences souhaitez-vous développer?" data-id="element-663" />
                </div>
                <div data-id="element-664">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-665">
                    Formations souhaitées
                  </label>
                  <textarea rows={3} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors" placeholder="Quelles formations vous intéressent?" data-id="element-666" />
                </div>
                <div data-id="element-667">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-668">
                    Aspirations de carrière
                  </label>
                  <textarea rows={3} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors" placeholder="Comment voyez-vous l'évolution de votre carrière?" data-id="element-669" />
                </div>
              </div>}
            {activeSection === 4 && <div className="space-y-6" data-id="element-670">
                <div data-id="element-671">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-672">
                    Commentaires généraux
                  </label>
                  <textarea rows={4} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors" placeholder="Partagez vos commentaires généraux sur l'année écoulée..." data-id="element-673" />
                </div>
                <div data-id="element-674">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-675">
                    Suggestions d'amélioration
                  </label>
                  <textarea rows={4} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors" placeholder="Quelles suggestions avez-vous pour améliorer l'organisation?" data-id="element-676" />
                </div>
                <div data-id="element-677">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-678">
                    Besoins et attentes
                  </label>
                  <textarea rows={3} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 transition-colors" placeholder="De quoi avez-vous besoin pour mieux réussir?" data-id="element-679" />
                </div>
              </div>}
            <div className="flex flex-col sm:flex-row justify-between gap-3 mt-8 pt-6 border-t border-gray-200" data-id="element-680">
              <button onClick={handleSave} className="flex items-center justify-center px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-681">
                <Save className="h-5 w-5 mr-2" data-id="element-682" />
                Sauvegarder
              </button>
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-683">
                {activeSection > 0 && <button onClick={() => setActiveSection(activeSection - 1)} className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-684">
                    Précédent
                  </button>}
                {activeSection < sections.length - 1 ? <button onClick={() => setActiveSection(activeSection + 1)} className="flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors" data-id="element-685">
                    Suivant
                    <ChevronRight className="h-5 w-5 ml-2" data-id="element-686" />
                  </button> : <button onClick={handleSubmit} className="flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors" data-id="element-687">
                    <CheckCircle className="h-5 w-5 mr-2" data-id="element-688" />
                    Soumettre l'évaluation
                  </button>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}